/**
 * F43 (Track B) — admin central report queue API client.
 *
 * `GET /admin/reports` UNIONs `user_reports` with F32's legacy `open_game_reports`
 * at read time (the read-mapper normalizes the legacy vocabulary + attaches the
 * synthetic `subjectType:'open_game'`). Each report deep-links into the owning
 * feature's admin surface; F43 owns the action only for `user` + `chat_message`
 * (the chat-context viewer + soft-delete live on the report-detail page).
 *
 * Bulk actions (`/admin/bulk/*`) carry an `Idempotency-Key` header (replay
 * returns the same `bulk_action_records` row) — see `bulk.ts`-less inline here.
 *
 * Component tests drive `reports.fixture.ts` (vitest alias). Production uses
 * these real `adminFetch` calls.
 */
import type {
  AdminPageMeta,
  BulkActionInput,
  BulkActionResponse,
  ChatContextQuery,
  ChatContextWindow,
  DismissReportInput,
  ReassignReportInput,
  ReportDetail,
  ReportListItem,
  ReportReason,
  ReportStatus,
  ResolveReportInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

export interface ReportListQuery {
  readonly subjectType?: string;
  readonly status?: ReportStatus | '';
  readonly reason?: ReportReason | '';
  readonly assignedAdminId?: string;
  readonly cursor?: string;
  readonly limit?: number;
  /** 1-based page index (page-numbered pagination). */
  readonly page?: number;
  /** Page size (default 25 server-side, max 100). */
  readonly perPage?: number;
}

export interface ReportListResponse {
  readonly data: readonly ReportListItem[];
  readonly meta: {
    readonly total: number;
    readonly open: number;
    readonly nextCursor: string | null;
    /** Present when `page` is supplied (page-numbered pagination). */
    readonly pagination?: AdminPageMeta;
  };
}

interface DetailEnvelope {
  readonly data: ReportDetail;
}

export type BulkActionKind = 'suspend' | 'dismiss-reports' | 'reassign-reports';

/** One assignable admin-staff option for the reassign affordances (PU-02/PU-10). */
export interface AssignableAdmin {
  readonly id: string;
  readonly label: string;
}

/** Raw F06 admin-staff list row (GET /admin/users — `admin_users:read`). */
interface AdminStaffRow {
  readonly id: string;
  readonly email: string;
  readonly displayName: string | null;
}

export const reportsApi = {
  async list(query: ReportListQuery): Promise<ReportListResponse> {
    const qs = buildQuery({
      subjectType: query.subjectType,
      status: query.status || undefined,
      reason: query.reason || undefined,
      assignedAdminId: query.assignedAdminId,
      cursor: query.cursor,
      limit: query.limit,
      page: query.page,
      perPage: query.perPage,
    });
    return adminFetch<ReportListResponse>(`/admin/reports${qs}`);
  },

  async getDetail(reportId: string): Promise<ReportDetail> {
    const res = await adminFetch<DetailEnvelope>(`/admin/reports/${encodeURIComponent(reportId)}`);
    return res.data;
  },

  async claim(reportId: string): Promise<ReportDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/reports/${encodeURIComponent(reportId)}/claim`,
      { method: 'POST', body: {} },
    );
    return res.data;
  },

  async unclaim(reportId: string): Promise<ReportDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/reports/${encodeURIComponent(reportId)}/unclaim`,
      { method: 'POST', body: {} },
    );
    return res.data;
  },

  async action(reportId: string, input: ResolveReportInput): Promise<ReportDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/reports/${encodeURIComponent(reportId)}/action`,
      { method: 'POST', body: input },
    );
    return res.data;
  },

  async dismiss(reportId: string, input: DismissReportInput): Promise<ReportDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/reports/${encodeURIComponent(reportId)}/dismiss`,
      { method: 'POST', body: input },
    );
    return res.data;
  },

  async reassign(reportId: string, input: ReassignReportInput): Promise<ReportDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/reports/${encodeURIComponent(reportId)}/reassign`,
      { method: 'POST', body: input },
    );
    return res.data;
  },

  async getChatContext(reportId: string, query: ChatContextQuery): Promise<ChatContextWindow> {
    const qs = buildQuery({ before: query.before, after: query.after });
    const res = await adminFetch<{ data: ChatContextWindow }>(
      `/admin/reports/${encodeURIComponent(reportId)}/chat-context${qs}`,
    );
    return res.data;
  },

  async softDeleteChatMessage(messageId: string, reason?: string): Promise<void> {
    await adminFetch<void>(`/admin/chat-messages/${encodeURIComponent(messageId)}/soft-delete`, {
      method: 'POST',
      body: reason ? { reason } : {},
    });
  },

  async bulk(
    kind: BulkActionKind,
    input: BulkActionInput,
    idempotencyKey: string,
  ): Promise<BulkActionResponse> {
    // PU-01 — the /admin/bulk/* routes wrap the result in a `{ data }` envelope
    // (mirrors adminUsersApi.bulkDelete); returning the raw body crashed the
    // BulkActionsDrawer results view AFTER the server had run the operation.
    const res = await adminFetch<{ data: BulkActionResponse }>(`/admin/bulk/${kind}`, {
      method: 'POST',
      body: input,
      headers: { 'idempotency-key': idempotencyKey },
    });
    return res.data;
  },

  /**
   * PU-06 — evidence lives in the PRIVATE SSE-KMS `report-evidence` bucket; the
   * stored `evidenceUrl` is an S3 key, not a fetchable URL. This issues a 5-min
   * presigned GET (the access is server-logged) for the ImageLightbox.
   */
  async getEvidenceDownloadUrl(reportId: string): Promise<{ url: string; expiresAt: string }> {
    const res = await adminFetch<{ data: { url: string; expiresAt: string } }>(
      `/admin/reports/${encodeURIComponent(reportId)}/evidence-download-url`,
    );
    return res.data;
  },

  /**
   * PU-02/PU-10 — the assignable admin-staff list for the reassign affordances
   * (bulk drawer + per-report dialog). Backed by F06's `GET /admin/users`
   * (`admin_users:read` — super_admin only at MVP). Roles without that
   * permission get a 403, which degrades to an empty list so the callers hide
   * their reassign options instead of rendering a permanently-disabled form.
   */
  async listAssignableAdmins(): Promise<readonly AssignableAdmin[]> {
    try {
      const res = await adminFetch<{ items: readonly AdminStaffRow[] }>(
        `/admin/users${buildQuery({ status: 'active', limit: 100 })}`,
      );
      return res.items.map((a) => ({ id: a.id, label: a.displayName ?? a.email }));
    } catch {
      // 403 (no admin_users:read) or transient failure → hide reassign options.
      return [];
    }
  },
};
